import { observer } from 'mobx-react-lite';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
} from 'motion/react';
import { useLayoutEffect, useRef, useState } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';

import {
  BIRD_SIZE,
  FLIGHT_DURATION_S,
  FLIGHT_WING_FLAP_S,
  SIDEBAR_LOGO_SIZE,
} from './bird-game.constants';
import { birdGameStore, type Point } from './bird-game.store';
import { type BirdGameController } from './use-bird-game';

const LOGO_SCALE = SIDEBAR_LOGO_SIZE / BIRD_SIZE;
const FLIGHT_EASE = [0.165, 0.84, 0.44, 1] as const;

const centerOf = (rect: DOMRect): Point => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
});

function findVisibleLogo(): Point | null {
  const logos = document.querySelectorAll<HTMLElement>('[data-bird-logo]');
  for (const logo of logos) {
    const rect = logo.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return centerOf(rect);
  }
  return null;
}

interface BirdFlightOverlayProps {
  controller: BirdGameController;
  onFlyOutArrive: () => void;
}

export const BirdFlightOverlay = observer(function BirdFlightOverlay({
  controller,
  onFlyOutArrive,
}: BirdFlightOverlayProps) {
  const { phase } = birdGameStore;

  if (phase === 'flying-in') {
    return (
      <Flight
        key="flying-in"
        resolveFrom={findVisibleLogo}
        resolveTo={controller.getSpawnPoint}
        fromScale={LOGO_SCALE}
        toScale={1}
        onArrive={() => birdGameStore.finishFlightIn()}
      />
    );
  }

  if (phase === 'flying-out') {
    return (
      <Flight
        key="flying-out"
        resolveFrom={() => birdGameStore.returnFrom}
        resolveTo={findVisibleLogo}
        fromScale={1}
        toScale={LOGO_SCALE}
        onArrive={onFlyOutArrive}
      />
    );
  }

  return null;
});

interface FlightProps {
  resolveFrom: () => Point | null;
  resolveTo: () => Point | null;
  fromScale: number;
  toScale: number;
  onArrive: () => void;
}

function Flight({
  resolveFrom,
  resolveTo,
  fromScale,
  toScale,
  onArrive,
}: FlightProps) {
  const reduceMotion = useReducedMotion();
  const wingAngle = useMotionValue(0);
  const [route, setRoute] = useState<{ from: Point; to: Point } | null>(null);

  const latest = useRef({ resolveFrom, resolveTo, onArrive });
  latest.current = { resolveFrom, resolveTo, onArrive };

  useLayoutEffect(() => {
    const from = latest.current.resolveFrom();
    const to = latest.current.resolveTo();
    if (!from || !to || reduceMotion) {
      latest.current.onArrive();
      return;
    }
    setRoute({ from, to });
    const wings = animate(wingAngle, [-20, -55, -20], {
      duration: FLIGHT_WING_FLAP_S,
      ease: 'easeInOut',
      repeat: Math.ceil(FLIGHT_DURATION_S / FLIGHT_WING_FLAP_S),
    });
    return () => wings.stop();
  }, [reduceMotion, wingAngle]);

  if (!route) return null;

  return (
    <motion.div
      aria-hidden
      className="fixed left-0 top-0 z-50 pointer-events-none flex items-center justify-center"
      style={{ width: BIRD_SIZE, height: BIRD_SIZE }}
      initial={{
        x: route.from.x - BIRD_SIZE / 2,
        y: route.from.y - BIRD_SIZE / 2,
        scale: fromScale,
      }}
      animate={{
        x: route.to.x - BIRD_SIZE / 2,
        y: route.to.y - BIRD_SIZE / 2,
        scale: toScale,
      }}
      transition={{ duration: FLIGHT_DURATION_S, ease: FLIGHT_EASE }}
      onAnimationComplete={() => latest.current.onArrive()}
    >
      <AppLogo size={BIRD_SIZE} wingAngle={wingAngle} />
    </motion.div>
  );
}
